// DHARNA 144 on canvas (docs/plans/2026-10-18-dharna-144.md): the design tokens of site/styles/tokens.css, and the
// wheatpaste primitives for every piece of UI the game draws on a canvas: tags, stamps, dockets and meters. No rounding,
// thick ink, shadows that are solid blocks; nothing glows, nothing blurs. tests/brand.test.ts keeps INK in step with
// the CSS.

export const INK = {
  newsprint: '#ECE0C8', newsprint2: '#DDCFB3', asphalt: '#0E0C09', inkDim: '#5A5246', newsprintDim: '#B5A890',
  metro: '#F8C40A', barricade: '#0A24B0', riso: '#E3342F', risoInk: '#B3261E',
} as const;

/**
 * The operation map's inks (docs/plans/2026-10-18-dharna-144.md, section 5): measured off the reference sheets. Warm
 * cream paper, roads and key buildings in solid ink, ordinary blocks a dark ink-grey, trees as near-black blots.
 */
export const MAP = {
  paper: '#E7D8BC', park: '#EEE2C8', block: '#2C2721', blockAlt: '#3A342B', key: '#0E0C09', road: '#0E0C09',
  lane: '#ECE0C8', tree: '#1E1A14',
} as const;

/** Canvas font strings in the brand's faces. Bebas Neue has one weight; it sets every label in capitals. */
export const FONT = {
  display: (px: number) => `400 ${px}px "Bebas Neue",Khand,Impact,sans-serif`,
  hi: (px: number, w = 600) => `${w} ${px}px Khand,"Bebas Neue",sans-serif`,
  body: (px: number, w = 500) => `${w} ${px}px Inter,system-ui,sans-serif`,
  mono: (px: number, w = 500) => `${w} ${px}px "IBM Plex Mono",ui-monospace,monospace`,
};

export interface Rect { x: number; y: number; w: number; h: number }

/** A block: the hard shadow, the fill, the ink border inside the edge. */
export function box(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, fill: string = INK.newsprint, bw = 2, shadow = 4, border: string = INK.asphalt) {
  x = Math.round(x); y = Math.round(y); w = Math.round(w); h = Math.round(h);
  if (shadow) { g.fillStyle = INK.asphalt; g.fillRect(x + shadow, y + shadow, w, h); }
  g.fillStyle = fill; g.fillRect(x, y, w, h);
  if (bw) { g.strokeStyle = border; g.lineWidth = bw; g.strokeRect(x + bw / 2, y + bw / 2, w - bw, h - bw); }
}

/** Diagonal ink hatching over a rectangle: the restricted-area fill, a meter's empty track. */
export function hatch(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, colour: string = INK.asphalt, gap = 6, lw = 1.5) {
  g.save(); g.beginPath(); g.rect(x, y, w, h); g.clip();
  g.strokeStyle = colour; g.lineWidth = lw; g.beginPath();
  for (let d = -h; d < w; d += gap) { g.moveTo(x + d, y + h); g.lineTo(x + d + h, y); }
  g.stroke(); g.restore();
}

export interface TagOpts { font?: string; fill?: string; ink?: string; padX?: number; padY?: number; bw?: number; shadow?: number; align?: 'left' | 'center' | 'right'; h?: number }

/** A one-line label in a box, its top at y and x at the edge `align` names. Returns where it went. */
export function tag(g: CanvasRenderingContext2D, text: string, x: number, y: number, o: TagOpts = {}): Rect {
  const font = o.font ?? FONT.mono(12), padX = o.padX ?? 8, bw = o.bw ?? 2;
  g.font = font;
  const tw = g.measureText(text).width, px = parseFloat(/(\d+(\.\d+)?)px/.exec(font)?.[1] ?? '12');
  const w = Math.ceil(tw + padX * 2), h = o.h ?? Math.round(px + (o.padY ?? 6) * 2);
  const left = o.align === 'center' ? x - w / 2 : o.align === 'right' ? x - w : x;
  box(g, left, y, w, h, o.fill ?? INK.newsprint, bw, o.shadow ?? 3);
  g.fillStyle = o.ink ?? INK.asphalt; g.textAlign = 'left'; g.textBaseline = 'middle';
  g.fillText(text, Math.round(left + padX), Math.round(y + h / 2 + (font.includes('Bebas') ? px * 0.06 : 0)));
  return { x: left, y, w, h };
}

/**
 * A stamp, centred on cx with its top at y: the title in newsprint capitals on an asphalt block, and under it, pasted
 * on, a metro-yellow strip with the line that says what to do. The logo's construction, at banner size.
 */
export function stamp(g: CanvasRenderingContext2D, title: string, sub: string, cx: number, y: number, size = 44, strip: string = INK.metro) {
  g.font = FONT.display(size);
  const tw = g.measureText(title).width;
  g.font = FONT.body(14, 600);
  const sw = sub ? g.measureText(sub).width : 0;
  const w = Math.ceil(Math.max(tw + 36, sw + 28)), th = Math.round(size * 1.05), sh = sub ? 30 : 0, x = Math.round(cx - w / 2);
  box(g, x, y, w, th + sh, INK.asphalt, 0, 6);
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.font = FONT.display(size); g.fillStyle = INK.newsprint; g.fillText(title, cx, y + th / 2 + size * 0.07);
  if (sub) {
    box(g, x, y + th, w, sh, strip, 3, 0);
    g.font = FONT.body(14, 600); g.fillStyle = INK.asphalt; g.fillText(sub, cx, y + th + sh / 2 + 1);
  }
  return { x, y, w, h: th + sh };
}

/** A meter: an ink frame, a hatched empty track and a solid fill to `frac`. */
export function meter(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, frac: number, fill: string = INK.metro) {
  x = Math.round(x); y = Math.round(y);
  g.fillStyle = INK.newsprint2; g.fillRect(x, y, w, h);
  hatch(g, x, y, w, h, 'rgba(14,12,9,.35)', 5, 1);
  g.fillStyle = fill; g.fillRect(x, y, Math.round(w * Math.max(0, Math.min(1, frac))), h);
  g.strokeStyle = INK.asphalt; g.lineWidth = 2; g.strokeRect(x - 1, y - 1, w + 2, h + 2);
}

/** Text with an ink outline, for labels that float over the street (names over heads): newsprint on asphalt. */
export function inked(g: CanvasRenderingContext2D, s: string, x: number, y: number, fill: string = INK.newsprint, lw = 3, stroke: string = INK.asphalt) {
  g.lineJoin = 'round'; g.lineWidth = lw; g.strokeStyle = stroke; g.fillStyle = fill;
  g.strokeText(s, x, y); g.fillText(s, x, y);
}
